import { differenceInCalendarDays } from 'date-fns';

/**
 * Recency buckets for the rail.
 *
 * `undated` is last and is not an error state: a project shared with someone who
 * has not sat in it yet has no last-activity stamp at all, and dropping those
 * rows would hide exactly the projects that most need starting.
 */
export type RecencyBucket = 'today' | 'yesterday' | 'last7' | 'last30' | 'older' | 'undated';

/** Fixed display order. The rail reads top-down as most-recent-first, so this is
 *  the order rather than whatever order the data arrived in. */
export const BUCKET_ORDER: readonly RecencyBucket[] = [
  'today',
  'yesterday',
  'last7',
  'last30',
  'older',
  'undated',
];

export interface RecencyGroup<T> {
  readonly bucket: RecencyBucket;
  readonly items: readonly T[];
}

/**
 * Which bucket a timestamp falls in.
 *
 * Calendar days, not elapsed hours: something from 11pm last night is
 * "yesterday" at 1am, and `differenceInHours` would call it "today" — which is
 * not what the person who wrote it at 11pm would call it.
 */
export function bucketFor(iso: string | null | undefined, now: Date): RecencyBucket {
  if (!iso) return 'undated';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return 'undated';

  // Every band is measured against the `now` that was passed in. `isToday` and
  // `isYesterday` read the real clock instead, which made this function disagree
  // with its own argument — correct in the app, where `now` IS the real clock,
  // and wrong anywhere a fixed date is supplied.
  const days = differenceInCalendarDays(now, parsed);

  // A stamp in the future is a clock-skew artefact, not a category. Treating it
  // as `today` keeps it at the top where it can be seen and questioned, rather
  // than filing it under `older` where a negative difference would send it.
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days <= 7) return 'last7';
  if (days <= 30) return 'last30';
  return 'older';
}

/**
 * Group items into recency buckets, newest bucket first, dropping the empty ones.
 *
 * Within a bucket the incoming order is preserved — the endpoints already sort by
 * last activity, and re-sorting here would silently fight them.
 */
export function groupByRecency<T>(
  items: readonly T[],
  getDate: (item: T) => string | null | undefined,
  now: Date = new Date(),
): readonly RecencyGroup<T>[] {
  const buckets = new Map<RecencyBucket, T[]>();

  for (const item of items) {
    const bucket = bucketFor(getDate(item), now);
    const existing = buckets.get(bucket);
    if (existing) existing.push(item);
    else buckets.set(bucket, [item]);
  }

  return BUCKET_ORDER.flatMap((bucket) => {
    const found = buckets.get(bucket);
    return found ? [{ bucket, items: found }] : [];
  });
}
